import axios from "axios"
import axiosRetry from 'axios-retry'

class Openai {

    threadId = null
    assistantId = null
    runId = null
    baseUrl = '//' + window.location.hostname
    type = null
    recordId = null
    pageId = null
    instructions = ''
    runStatus = null
    feedback = ''
    grade = null
    aiScoreFailed = false
    completedRunIds = []
    scoringPollTimer = null
    feedbackPollTimer = null
    aborted = false
    abortController = new AbortController()
    functionCallsHandled = false

    constructor(type = null, assistantId = null, recordId = null, pageId = null) {
        this.type = type
        this.recordId = recordId
        this.pageId = pageId
        this.assistantId = assistantId
    }

    abortOperations() {
        this.aborted = true
        this.killFeedbackPollTimer()
        this.killScoringPollTimer()
        this.abortController.abort()
        this.abortController = new AbortController() // Reset for future operations
    }

    stripHtml(html) {
        return html.replace(/<[^>]*>?/g, '')
    }

    setInstructions(instructions) {
        this.instructions = instructions
    }

    setMessagesCallback(callback) {
        this.getMessagesCallback = callback
    }

    setScoreCallback(callback) {
        this.getScoreCallback = callback
    }

    getScoringAssistants = async () => {
        axiosRetry(axios, {
            retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                return retryCount * 1000
            }
        })
        let resp = await axios.get(`//${window.location.hostname}/api/openai/assistant/?type=scoring`)
        return resp.data
    }

    getScoringAssistantById = async (id) => {
        axiosRetry(axios, {
            retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                return retryCount * 1000
            }
        })
        let resp = await axios.get(`//${window.location.hostname}/api/openai/assistant/${id}`)
        return resp.data
    }

    getAssistantsByType = async (type) => {
        axiosRetry(axios, {
            retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                return retryCount * 1000
            }
        })
        let resp = await axios.get(`//${window.location.hostname}/api/openai/assistant/?type=${type}`)
        return resp.data
    }

    async createThread() {
        try {
            axiosRetry(axios, {
                retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                    return retryCount * 1000
                }
            })
            let resp = await axios.post(`${this.baseUrl}/api/openai/thread`, {
                type: this.type,
                record_id: this.recordId,
                page_id: this.pageId
            })

            this.threadId = resp.data.id

            return true
        } catch (e) {
            console.log(e)
            return false
        }
    }

    async addMessageToThread(content, runAssistant = true, role = 'user') {
        try {
            axiosRetry(axios, {
                retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                    return retryCount * 1000
                }
            })
            await axios.post(`${this.baseUrl}/api/openai/thread/${this.threadId}/message`, {
                role: role,
                content: this.stripHtml(content),
                signal: this.abortController.signal
            })
            if (runAssistant) {
                await this.runAssistant()
            }
        } catch (e) {
            if (e.name === 'AbortError') {
                console.log('addMessageToThread was aborted')
            } else {
                console.log(e)
            }
        }
    }

    async runAssistant() {
        try {
            axiosRetry(axios, {
                retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                    return retryCount * 1000
                }
            })

            // There can only ever be one run in progress at a time so let's check for that and use it, so we don't create a new run and error out.
            // This can happen if a prior call to runAssistant failed or timed out
            let runs = await axios.get(`${this.baseUrl}/api/openai/thread/${this.threadId}/runs`, {
                signal: this.abortController.signal
            })
            if (runs.data.data.length > 0) {

                // filter runs where status is in_progress
                let inProgress = runs.data.data.filter((run) => {
                    return run.status === 'in_progress'
                })

                // if there are in_progress runs we want to use the first one
                if (inProgress.length > 0) {
                    this.runId = inProgress[0].id
                    if (this.type === 'feedback' || this.type === 'meta') {
                        this.pollStatus()
                    } else if (this.type === 'scoring') {
                        this.pollScoringStatus()
                    }
                    return
                }
            }

            // Create a new run if we didn't find an existing one
            let data = {
                assistant_id: this.assistantId,
                additional_instructions: this.instructions
            }
            if (this.type === 'scoring') {
                data.additional_instructions = 'Please score the following response using the evaluation rubric provided in the assistants instructions.'
                data.force_function_calls = true;
            }

            let resp = await axios.post(`${this.baseUrl}/api/openai/thread/${this.threadId}/run`, data)

            // if resp.data.id is undefined throw an error
            if (!resp.data.id) {
                throw new Error('Run ID is undefined')
            }

            this.runId = resp.data.id
            if (this.type === 'feedback' || this.type === 'meta') {
                this.pollStatus()
            } else if (this.type === 'scoring') {
                this.pollScoringStatus()
            }
        } catch (e) {
            if (e.name === 'AbortError') {
                console.log('runAssistant was aborted')
            } else {
                console.log(e)
            }
        }
    }

    async pollStatus() {
        clearInterval(this.feedbackPollTimer)
        this.feedbackPollTimer = setInterval(async () => {
            try {
                let response = await axios.get(`${this.baseUrl}/api/openai/thread/${this.threadId}/runs/${this.runId}`, {
                    signal: this.abortController.signal
                })
                this.runStatus = response.data.status
                if (response.data.status === 'completed') {
                    clearInterval(this.feedbackPollTimer)
                    if (!this.completedRunIds.includes(this.runId)) {
                        await this.getMessages()
                    }
                    this.completedRunIds.push(this.runId)
                }
            } catch (e) {
                if (e.name === 'AbortError') {
                    console.log('pollStatus was aborted')
                    this.killFeedbackPollTimer()
                } else {
                    console.log(e)
                }
            }
        }, 1000)
    }

    killFeedbackPollTimer() {
        console.log('killing feedback poll timer')
        clearInterval(this.feedbackPollTimer)
    }

    async pollScoringStatus() {
        clearInterval(this.scoringPollTimer)
        this.scoringPollTimer = setInterval(async () => {
            try {
                let response = await axios.get(`${this.baseUrl}/api/openai/thread/${this.threadId}/runs/${this.runId}`, {
                    signal: this.abortController.signal
                })
                if (response.data.status === 'requires_action') {
                    console.log('polling score status')
                    const functions = response.data.required_action.submit_tool_outputs.tool_calls.filter((tool_call) => {
                        return tool_call.type === 'function'
                    })
                    this.handleFunctionCalls(functions)
                    this.killScoringPollTimer()
                }
                if (response.data.status === 'completed') {
                    this.killScoringPollTimer()
                    console.log('score status complete')
                    if (!this.completedRunIds.includes(this.runId)) {
                        await this.getMessages()
                    }
                    this.completedRunIds.push(this.runId)
                }
            } catch (e) {
                if (e.name === 'AbortError') {
                    console.log('pollScoringStatus was aborted')
                    this.killScoringPollTimer()
                } else {
                    console.log(e)
                }
            }
        }, 1000)
    }

    killScoringPollTimer() {
        console.log('killing scoring poll timer')
        clearInterval(this.scoringPollTimer)
    }

    handleFunctionCalls(funcs) {
        // we only want to handle the function calls from
        // a scoring assistant once so just return if this
        // ends up getting called again in the event we get
        // more than one response with a requires_action status
        if (this.aborted || this.functionCallsHandled) {
            console.log('aborting handleFunctionCalls')
            return
        }
        console.log('FUNCTION CALLS')
        console.log(funcs)
        let grade = {}
        funcs.forEach((func) => {
            let args = JSON.parse(func.function.arguments)
            Object.entries(args).forEach(([key, value]) => {
                grade[key] = value
            })
        })
        this.grade = grade

        let outputs = []
        funcs.forEach((func) => {
            outputs.push({
                tool_call_id: func.id,
                output: 'ok'
            })
        })

        this.submitFunctionOutputs(outputs, this.grade)
        this.grade['assistantId'] = this.assistantId
        if (this.getScoreCallback) {
            if (this.grade['meter']) {
                // normalize this value since OpenAI randomly capitalizes it sometimes
                this.grade['meter'] = this.grade['meter'].toLowerCase()
            }
            this.getScoreCallback(this.grade)
        }
        this.functionCallsHandled = true
    }

    async submitFunctionOutputs(outputs = [], grade = {}) {

        const data = {
            outputs: {
                tool_outputs: outputs
            },
            grade: grade,
            signal: this.abortController.signal
        }
        try {
            axiosRetry(axios, {
                retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                    return retryCount * 1000
                }
            })
            await axios.post(`${this.baseUrl}/api/openai/thread/${this.threadId}/runs/${this.runId}`, data)
            await this.pollScoringStatus(this.threadId, this.runId)
        } catch (e) {
            if (e.name === 'AbortError') {
                console.log('submitFunctionOutputs was aborted')
            } else {
                console.log(e)
            }
        }
    }

    async getMessages() {
        try {
            axiosRetry(axios, {
                retries: 3, retryCondition: () => true, retryDelay: (retryCount) => {
                    return retryCount * 1000
                }
            })
            let resp = await axios.get(`${this.baseUrl}/api/openai/thread/${this.threadId}/messages`, {
                signal: this.abortController.signal
            })

            let messages = resp.data.data.map((message) => {
                let m = {}
                m.role = message.role
                m.text = message.content[0]?.text?.value || ''
                return m
            })

            // reverse the messages so the most recent is at the bottom
            messages.reverse()

            this.feedback = resp.data.data[0].content[0]?.text?.value || ''

            if (this.getMessagesCallback) {
                console.log('calling getMessagesCallback')
                this.getMessagesCallback(this, messages)
            }

        } catch (e) {
            if (e.name === 'AbortError') {
                console.log('getMessages was aborted')
            } else {
                console.log(e)
            }
        }
    }
}

export default Openai
